import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { KibMultiSelectionOption } from '@chewy/kib-fields-new-react'
import { useDebounce } from 'use-debounce'
import { Defaults } from '@pbt/pbt-ui-components'

import {
  useGetRoleIdsList,
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import SecretSearchContext from '~/constants/secretSearchContext'
import {
  clearSuggestionResults,
  fetchSuggestionResults,
} from '~/store/actions/search'
import {
  getSecretSearchIsReceiving,
  getSuggestionResults,
} from '~/store/reducers/search'
import { getUser, getUsersMap } from '~/store/reducers/users'
import type { SuggestionResult, TeamFilter } from '~/types'

import { KibUserSelectBase } from './KibUserSelectBase'
import {
  buildUserOption,
  mapSuggestionResultsToOptions,
} from './userSelectUtils'

export interface KibPreloadedUserSelectProps {
  disabled?: boolean
  filterScope?: UserSelectFilterScope
  includeInactive?: boolean
  label?: string
  onChange?: (e: string) => void
  placeHolder: string
  readOnly?: boolean
  required?: boolean
  reset?: boolean
  teamFilter?: TeamFilter
  useInMemory?: boolean
  userSelectStateId: string
  value?: string
}

export const KibPreloadedUserSelect = ({
  reset = false,
  label,
  userSelectStateId,
  includeInactive = false,
  required = false,
  teamFilter,
  placeHolder,
  disabled,
  onChange,
  value,
  readOnly,
  useInMemory,
  filterScope = UserSelectFilterScope.MainStaff,
}: KibPreloadedUserSelectProps) => {
  const dispatch = useDispatch()
  const searchResults: SuggestionResult[] = useSelector(getSuggestionResults)
  const usersMap = useSelector(getUsersMap)
  const [internalValue, setInternalValue] = useState<KibMultiSelectionOption[]>(
    [],
  )
  const [rawInputValue, setRawInputValue] = useState('')
  const [inputValue] = useDebounce(rawInputValue, Defaults.DEBOUNCE_ACTION_TIME)
  const hasInitialSynced = useRef(false)
  const roleIdsList = useGetRoleIdsList(filterScope)
  const selectedUser = useSelector(getUser(value))
  const isLoading = useSelector(getSecretSearchIsReceiving)

  useEffect(() => {
    if (!hasInitialSynced.current && value) {
      const userOption = buildUserOption(usersMap, value)
      if (userOption) {
        hasInitialSynced.current = true
        setInternalValue([userOption])
      }
    }
  }, [value, usersMap])

  const fetchSuggestions = () => {
    if (roleIdsList.length > 0) {
      dispatch(
        fetchSuggestionResults({
          searchContext: SecretSearchContext.PERSONS,
          searchTerm: '',
          teamFilter,
          roleIds: roleIdsList.join(','),
          includeInactive,
          shorten: true,
          maxCount: 100,
        }),
      )
    }
  }

  const userOptions: KibMultiSelectionOption[] = useMemo(() => {
    const options = mapSuggestionResultsToOptions(searchResults)

    if (
      selectedUser &&
      !options.some((option) => option.value === selectedUser.id)
    ) {
      const selectedUserOption = buildUserOption(usersMap, selectedUser.id)
      if (selectedUserOption) {
        options.push(selectedUserOption)
      }
    }

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [searchResults, usersMap, selectedUser])

  const onQuery = (
    v?: string,
    e?:
      | ChangeEvent<HTMLInputElement>
      | ReactMouseEvent<HTMLElement>
      | FocusEvent<HTMLInputElement>
      | KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e?.type === 'change') {
      setRawInputValue(v || '')
      if (!v) {
        fetchSuggestions()
      }
    }
  }

  const onFocus = () => {
    fetchSuggestions()
  }

  const onBlur = () => {
    if (value) {
      const userOption = buildUserOption(usersMap, value)
      if (userOption) {
        setRawInputValue('')
        setInternalValue([userOption])
      }
    }
    dispatch(clearSuggestionResults())
  }

  return (
    <KibUserSelectBase
      disabled={disabled}
      filterScope={filterScope}
      includeInactive={includeInactive}
      inputValue={inputValue}
      internalValue={internalValue}
      isLoading={isLoading}
      label={label}
      placeHolder={placeHolder}
      readOnly={readOnly}
      required={required}
      reset={reset}
      setInternalValue={(selectedValues) => setInternalValue(selectedValues)}
      teamFilter={teamFilter}
      useInMemory={useInMemory}
      userOptions={userOptions}
      userSelectStateId={userSelectStateId}
      usersMap={usersMap}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onQuery={onQuery}
    />
  )
}
